import { useState, useEffect, useCallback, useRef } from "react";
import { isAxiosError } from "axios";
import {
  getLabels,
  createLabel,
  updateLabel,
  deleteLabel,
  setArticleLabel,
  getArticleLabels,
  GrantsLabelItem,
  ArticleLabelMap,
} from "../../../services/grantsApi";

interface UseLabelsOptions {
  articleType: "kfs" | "bur";
  articleIds: string[];
}

export function useLabels({ articleType, articleIds }: UseLabelsOptions) {
  const [labels, setLabels] = useState<GrantsLabelItem[]>([]);
  const [articleLabels, setArticleLabels] = useState<ArticleLabelMap>({});
  const [loading, setLoading] = useState(true);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stabilize articleIds by comparing contents, not reference
  const articleIdsKey = articleIds.join(",");
  const articleIdsRef = useRef(articleIds);
  if (articleIdsKey !== articleIdsRef.current.join(",")) {
    articleIdsRef.current = articleIds;
  }
  const stableArticleIds = articleIdsRef.current;

  // Fetch tenant labels
  const fetchLabels = useCallback(async () => {
    try {
      const data = await getLabels();
      setLabels(data);
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setError(err.response?.data?.message || "Błąd pobierania etykiet");
      } else {
        setError("Błąd pobierania etykiet");
      }
    }
  }, []);

  // Fetch article-label assignments for current page
  const fetchArticleLabels = useCallback(async () => {
    if (stableArticleIds.length === 0) {
      setArticleLabels({});
      return;
    }
    setLabelsLoading(true);
    try {
      const data = await getArticleLabels(articleType, stableArticleIds);
      setArticleLabels(data);
    } catch (err: unknown) {
      // Non-critical - don't block UI
      console.error("Failed to fetch article labels:", err);
    } finally {
      setLabelsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleType, articleIdsKey]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchLabels(), fetchArticleLabels()]).finally(() =>
      setLoading(false)
    );
  }, [fetchLabels, fetchArticleLabels]);

  const handleCreateLabel = useCallback(
    async (name: string, color: string) => {
      const created = await createLabel({ name, color });
      setLabels((prev) => [...prev, created]);
      return created;
    },
    []
  );

  const handleUpdateLabel = useCallback(
    async (
      id: string,
      payload: { name?: string; color?: string; sortOrder?: number }
    ) => {
      const updated = await updateLabel(id, payload);
      setLabels((prev) => prev.map((l) => (l.id === id ? updated : l)));
      // Update articleLabels that reference this label
      setArticleLabels((prev) => {
        const next = { ...prev };
        for (const [artId, info] of Object.entries(next)) {
          if (info.labelId === id) {
            next[artId] = {
              ...info,
              labelName: updated.name,
              color: updated.color,
            };
          }
        }
        return next;
      });
      return updated;
    },
    []
  );

  const handleDeleteLabel = useCallback(async (id: string) => {
    await deleteLabel(id);
    setLabels((prev) => prev.filter((l) => l.id !== id));
    // Remove article-label assignments for this label
    setArticleLabels((prev) => {
      const next = { ...prev };
      for (const [artId, info] of Object.entries(next)) {
        if (info.labelId === id) {
          delete next[artId];
        }
      }
      return next;
    });
  }, []);

  const handleSetArticleLabel = useCallback(
    async (articleId: string, labelId: string | null) => {
      const result = await setArticleLabel(articleType, articleId, labelId);
      setArticleLabels((prev) => {
        const next = { ...prev };
        if (result.labelId === null) {
          delete next[articleId];
        } else {
          const label = labels.find((l) => l.id === result.labelId);
          if (label) {
            next[articleId] = {
              labelId: label.id,
              labelName: label.name,
              color: label.color,
            };
          }
        }
        return next;
      });
      return result;
    },
    [articleType, labels]
  );

  return {
    labels,
    articleLabels,
    loading,
    labelsLoading,
    error,
    createLabel: handleCreateLabel,
    updateLabel: handleUpdateLabel,
    deleteLabel: handleDeleteLabel,
    setArticleLabel: handleSetArticleLabel,
    refetchLabels: fetchLabels,
  };
}
